<script lang="ts">
	import { wikiStyles } from '$lib/components/common/ui/wiki';
</script>

<article class="max-w-3xl mx-auto">
	<h1 class={wikiStyles.headerText}>Participatory Finance</h1>

	<p class={wikiStyles.paragraphText}>
		Participatory finance represents a fundamental shift in how financial decisions are made, moving
		from centralized control to community-led processes. This approach ensures that those most
		affected by financial decisions have a voice in how resources are allocated and managed.
	</p>

	<h2 class={wikiStyles.subheaderText}>Key Approaches</h2>
	<ul class={wikiStyles.ulText}>
		<li>Citizen-Led Finance - Community leadership in local investment decisions</li>
		<li>
			Participatory Budgeting - A democratic process where community members directly decide how to
			spend part of a public budget. First developed in Porto Alegre, Brazil in 1989, it typically
			involves residents brainstorming project ideas, developing proposals, voting on which projects
			to fund, and monitoring implementation. Cities worldwide now use PB for portions of municipal
			budgets, schools, housing authorities, and other public funds.
		</li>
		<li>Web3 Governance - Blockchain-based decision making</li>
		<li>
			Funder-Field Partnerships - Collaborative resource allocation where philanthropists or fund
			managers and movement leaders work together to strategically allocate funds
		</li>
		<li>
			Landscape Finance - Engaging local businesses and stakeholders to identify and address
			regional gaps, commonly used in regenerative agriculture and landscape restoration efforts
			aimed at reducing flooding and drought risks.
		</li>
	</ul>

	<h2 class={wikiStyles.subheaderText}>Implementation Models</h2>
	<div class={wikiStyles.paragraphText}>
		<p class="mb-4">Successful participatory finance examples include:</p>
		<ul class={wikiStyles.ulText}>
			<li>
				<strong>WEPOWER</strong> - Neighborhood citizen-led investment model
			</li>
			<li>
				<strong>Boston Ujima Project</strong> - The U.S.'s first democratically governed investment fund,
				where community members collectively control $1.9M+ invested in BIPOC-owned businesses and cooperatives.
				Their model combines democratic assemblies, community standards, and participatory investment
				planning.
			</li>
			<li>
				<strong>DAO Governance</strong> - Token-weighted decision making
			</li>
			<li>
				<strong>Community Land Trusts</strong> - Collective land ownership and management
			</li>
		</ul>
	</div>

	<h2 class={wikiStyles.subheaderText}>Incentive Structures</h2>
	<p class={wikiStyles.paragraphText}>Innovative approaches to encourage participation:</p>
	<ul class={wikiStyles.ulText}>
		<li>Equity multipliers for regenerative actions</li>
		<li>Reputation-based voting power</li>
		<li>Community contribution rewards</li>
		<li>
			<a class="underline text-bloomBlueNormal" href="/learn/whitepaper"
				>Bloom Network's Regenerative Economic Protocol</a
			>
		</li>
	</ul>

	<h2 class={wikiStyles.subheaderText}>Benefits</h2>
	<ul class={wikiStyles.ulText}>
		<li>More equitable resource distribution</li>
		<li>Increased community engagement</li>
		<li>Better alignment with local needs</li>
		<li>Enhanced transparency and accountability</li>
	</ul>

	<h2 class={wikiStyles.subheaderText}>Related Articles</h2>
	<ul class="space-y-2 text-bloomBlueLightActive">
		<li>
			<a href="/learn/wiki/federated-cooperatives" class="underline">Federated Cooperatives</a>
		</li>
		<li><a href="/learn/wiki/local-currencies" class="underline">Local Currencies</a></li>
		<li><a href="/learn/wiki/gift-economy" class="underline">Gift Economy</a></li>
	</ul>
</article>
